import { refreshWaiting } from './waiting'
import { supabase } from './supabase'

/**
 * Surveys (310, David 25 Oct 2026).
 *
 * A Survey manager makes a survey, chooses who is asked (everybody, a part of
 * the company, named people, a role), opens it, and shows the answers live on
 * a screen: a word cloud, a list or bars. People answer SIGNED IN — scanning
 * the QR code is also how a room full of managers gets signed in on their
 * phones. A survey either records names (`identified`) or keeps 013/B15's
 * anonymous rules; that choice is fixed once anybody has answered.
 *
 * Every read and write is a SECURITY DEFINER function that checks the caller;
 * nothing here decides who may do what. The word cloud is worked out HERE,
 * from the answers the server returns, so the presenter can switch display
 * without another round trip — and so the counting is a pure function the
 * suite can test.
 */

export type SurveyStatus = 'draft' | 'open' | 'closed'
/** 314. On the Surveys page a survey its owners put away reads 'archived'. */
export type SurveyRowStatus = SurveyStatus | 'archived'
export type SurveyAccess = 'owner' | 'viewer'
export type ItemKind = 'words' | 'text' | 'single' | 'multi' | 'scale' | 'ideas'
export type ItemDisplay = 'auto' | 'cloud' | 'list' | 'bars' | 'pills'
export type AudienceKind = 'all' | 'org_unit' | 'person' | 'role'

export const ITEM_KINDS: readonly { key: ItemKind; label: string; hint: string }[] = [
  { key: 'words', label: 'A few words', hint: 'Short answers, shown as a word cloud.' },
  { key: 'text', label: 'Longer answer', hint: 'A sentence or two, shown as a list.' },
  { key: 'single', label: 'Choose one', hint: 'Shown as bars.' },
  { key: 'multi', label: 'Choose any', hint: 'Shown as bars.' },
  { key: 'scale', label: 'Rating 1 to 5', hint: 'Shown as bars and an average.' },
  // 317 (David, 27 Oct): brainstorming - Enter sends one idea, then the next.
  { key: 'ideas', label: 'Ideas', hint: 'People add as many as they like, one at a time. Shown as pills.' },
]

export type SurveyRow = {
  surveyId: string
  title: string
  description: string | null
  status: SurveyRowStatus
  identified: boolean
  myAccess: SurveyAccess | null
  invited: boolean
  responded: boolean
  itemCount: number
  submissions: number | null
  closesAt: string | null
  openedAt: string | null
  createdAt: string
}

export type SurveyItem = {
  itemId: string
  kind: ItemKind
  prompt: string
  choices: string[]
  required: boolean
  display: ItemDisplay
}

export type MyAnswer = { text?: string | null; choices?: number[] | null; value?: number | null }

export type SurveyToAnswer = {
  surveyId: string
  title: string
  description: string | null
  status: SurveyStatus
  identified: boolean
  closesAt: string | null
  myAccess: SurveyAccess | null
  invited: boolean
  mayAnswer: boolean
  maySeeResults: boolean
  responded: boolean
  /** 311: anybody signed in with the link may answer. */
  linkAccess: boolean
  /** 312: the question the presenter has open; null = answer at your own pace. */
  liveItemId: string | null
  /** 312: the questions you have answered (named or anonymous). */
  answeredItems: string[]
  items: SurveyItem[]
  myAnswers: Record<string, MyAnswer>
  /** 317. The form's questions (not ideas) have been sent - an anonymous
   *  survey takes the form once; ideas keep coming. */
  formSent: boolean
  /** 317. Named: your ideas per question, oldest first, to take back. */
  myIdeas: Record<string, MyIdea[]>
  /** 317. How many ideas you have added per question (named or anonymous). */
  myIdeaCounts: Record<string, number>
}

export type MyIdea = { answerId: string; text: string }

/** 317. The most ideas one person may add to one question (the server's cap). */
export const IDEAS_MAX = 30

export type AnswerDraft = { text: string; choices: number[]; value: number | null }

export type ResultAnswer = {
  answerId: string | null
  text: string
  name: string | null
  at: string | null
  hidden: boolean
}

export type ResultItem = {
  itemId: string
  kind: ItemKind
  display: ItemDisplay
  prompt: string
  choices: string[]
  answered: number | null
  /** 317. How many PEOPLE answered this question (answered counts answers). */
  people: number | null
  counts: number[] | null
  scale: number[] | null
  answers: ResultAnswer[] | null
  hiddenWords: string[]
}

export type LiveResults = {
  surveyId: string
  title: string
  /** 312. The question the room can answer right now; null = not run live. */
  liveItemId: string | null
  status: SurveyStatus
  identified: boolean
  myAccess: SurveyAccess | null
  submissions: number
  suppressed: boolean
  minToShow: number
  items: ResultItem[]
}

export type AudienceRow = {
  kind: AudienceKind
  orgUnitId: string | null
  personId: string | null
  roleKey: string | null
  label: string
}

export type SurveyPerson = { personId: string; access: SurveyAccess; name: string }

/** 311. Part of the company: a Where filter (Country > Legal entity > Business
 *  unit) AND a Department filter. Several choices in one filter mean any of
 *  them. `whereChoices` is what the picker held (a country stays a country on
 *  screen); `whereIds` is what it resolved to when saved. */
export type AudienceGroup = {
  whereChoices: string[]
  whereIds: string[]
  departmentIds: string[]
  whereNames: string[]
  departmentNames: string[]
}

export type EditorItem = Omit<SurveyItem, 'itemId'> & { itemId: string | null }

export type SurveyEditor = {
  surveyId: string
  title: string
  description: string | null
  identified: boolean
  state: SurveyStatus
  status: SurveyStatus
  /** 314. Set while it is archived (closed, off everybody's page but its owners'). */
  archivedAt: string | null
  closesAt: string | null
  resultsToAudience: boolean
  linkAccess: boolean
  openedAt: string | null
  submissions: number
  answeredItems: string[]
  invitedCount: number
  items: EditorItem[]
  audience: AudienceRow[]
  groups: AudienceGroup[]
  people: SurveyPerson[]
}

type J = Record<string, unknown>
const str = (v: unknown) => (typeof v === 'string' ? v : null)
const arr = <T,>(v: unknown, f: (x: unknown) => T): T[] => (Array.isArray(v) ? v.map(f) : [])

function toItem(x: unknown): SurveyItem {
  const r = x as J
  return {
    itemId: String(r.item_id),
    kind: r.kind as ItemKind,
    prompt: str(r.prompt) ?? '',
    choices: arr(r.choices, (c) => String(c)),
    required: r.required === true,
    display: (r.display as ItemDisplay) ?? 'auto',
  }
}

export async function loadMySurveys(): Promise<SurveyRow[]> {
  const { data, error } = await supabase.rpc('my_surveys')
  if (error) throw error
  return ((data ?? []) as J[]).map((r) => ({
    surveyId: String(r.survey_id),
    title: str(r.title) ?? '',
    description: str(r.description),
    status: r.status as SurveyRowStatus,
    identified: r.identified === true,
    myAccess: (r.my_access as SurveyAccess | null) ?? null,
    invited: r.invited === true,
    responded: r.responded === true,
    itemCount: Number(r.item_count ?? 0),
    submissions: r.submissions == null ? null : Number(r.submissions),
    closesAt: str(r.closes_at),
    openedAt: str(r.opened_at),
    createdAt: String(r.created_at),
  }))
}

export async function loadSurveyToAnswer(id: string): Promise<SurveyToAnswer> {
  const { data, error } = await supabase.rpc('survey_to_answer', { p_survey: id })
  if (error) throw error
  const r = data as J
  const mine: Record<string, MyAnswer> = {}
  for (const [k, v] of Object.entries((r.my_answers ?? {}) as Record<string, J>)) {
    mine[k] = {
      text: str(v.text),
      choices: Array.isArray(v.choices) ? (v.choices as number[]) : null,
      value: typeof v.value === 'number' ? v.value : null,
    }
  }
  return {
    surveyId: String(r.survey_id),
    title: str(r.title) ?? '',
    description: str(r.description),
    status: r.status as SurveyStatus,
    identified: r.identified === true,
    closesAt: str(r.closes_at),
    myAccess: (r.my_access as SurveyAccess | null) ?? null,
    invited: r.invited === true,
    mayAnswer: r.may_answer === true,
    maySeeResults: r.may_see_results === true,
    responded: r.responded === true,
    linkAccess: r.link_access === true,
    liveItemId: str(r.live_item_id),
    answeredItems: arr(r.answered_items, String),
    items: arr(r.items, toItem),
    myAnswers: mine,
    formSent: r.form_sent === true,
    myIdeas: Object.fromEntries(
      Object.entries((r.my_ideas ?? {}) as Record<string, J[]>).map(([k, v]) => [
        k,
        (Array.isArray(v) ? v : []).map((x) => ({ answerId: String(x.answer_id), text: str(x.text) ?? '' })),
      ]),
    ),
    myIdeaCounts: Object.fromEntries(
      Object.entries((r.my_idea_counts ?? {}) as Record<string, unknown>).map(([k, v]) => [k, Number(v ?? 0)]),
    ),
  }
}

/** The draft a form starts from: your saved answers on an identified survey,
 *  otherwise empty. */
export function draftsFrom(s: SurveyToAnswer): Record<string, AnswerDraft> {
  const out: Record<string, AnswerDraft> = {}
  for (const it of s.items) {
    const m = s.myAnswers[it.itemId]
    out[it.itemId] = { text: m?.text ?? '', choices: m?.choices ?? [], value: m?.value ?? null }
  }
  return out
}

/** One question's answer as the server takes it, or null when it is empty. */
export function oneAnswer(it: SurveyItem, d: AnswerDraft | undefined): { text?: string; choices?: number[]; value?: number } | null {
  if (!d) return null
  if ((it.kind === 'words' || it.kind === 'text') && d.text.trim()) return { text: d.text.trim() }
  if ((it.kind === 'single' || it.kind === 'multi') && d.choices.length) return { choices: d.choices }
  if (it.kind === 'scale' && d.value != null) return { value: d.value }
  return null
}

/** A person's own answer to one question, as a line to show back to them
 *  ("Tea", "4 / 5"), or null when there is nothing to show (none, or an
 *  anonymous survey, whose answers are never handed back). */
export function answerSummary(it: SurveyItem, m: MyAnswer | undefined): string | null {
  if (!m) return null
  if ((it.kind === 'words' || it.kind === 'text') && m.text?.trim()) return m.text.trim()
  if ((it.kind === 'single' || it.kind === 'multi') && m.choices?.length) {
    return m.choices.map((i) => it.choices[i]).filter(Boolean).join(', ') || null
  }
  if (it.kind === 'scale' && m.value != null) return `${m.value} / 5`
  return null
}

/** What answer_survey() takes: only the items that have something in them. */
export function answersPayload(items: SurveyItem[], drafts: Record<string, AnswerDraft>) {
  const out: { item_id: string; text?: string; choices?: number[]; value?: number }[] = []
  for (const it of items) {
    if (it.kind === 'ideas') continue // 317: ideas are sent one by one, never with the form
    const a = oneAnswer(it, drafts[it.itemId])
    if (a) out.push({ item_id: it.itemId, ...a })
  }
  return out
}

/** 312. One question, answered while the survey is run live. */
export async function answerSurveyItem(id: string, item: SurveyItem, draft: AnswerDraft) {
  const { error } = await supabase.rpc('answer_survey_item', { p_survey: id, p_item: item.itemId, p_answer: oneAnswer(item, draft) ?? {} })
  if (error) throw error
  refreshWaiting() // 325: a count on the nav or the bell may have moved
}

/** 317. One idea, sent the moment it is entered. Returns its id on a named
 *  survey (so it can be taken back), null on an anonymous one. */
export async function addSurveyIdea(id: string, itemId: string, text: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('add_survey_idea', { p_survey: id, p_item: itemId, p_text: text })
  if (error) throw error
  return typeof data === 'string' ? data : null
}

/** 317. Take back one of your own ideas (named surveys only). */
export async function removeSurveyIdea(answerId: string) {
  const { error } = await supabase.rpc('remove_survey_idea', { p_answer: answerId })
  if (error) throw error
}

/** 317. Does this survey's form have anything to send (questions that are not ideas)? */
export function hasFormQuestions(items: SurveyItem[]): boolean {
  return items.some((i) => i.kind !== 'ideas')
}

/** 312. The presenter opens one question for everybody, or (null) stops. */
export async function setSurveyLive(id: string, itemId: string | null) {
  const { error } = await supabase.rpc('set_survey_live', { p_survey: id, p_item: itemId })
  if (error) throw error
}

/** Required items with nothing in them, by prompt — said before sending, so
 *  the server's refusal is the second line rather than the first. */
export function missingRequired(items: SurveyItem[], drafts: Record<string, AnswerDraft>): string[] {
  const sent = new Set(answersPayload(items, drafts).map((a) => a.item_id))
  return items.filter((i) => i.required && !sent.has(i.itemId)).map((i) => i.prompt)
}

export async function answerSurvey(id: string, items: SurveyItem[], drafts: Record<string, AnswerDraft>) {
  const { error } = await supabase.rpc('answer_survey', { p_survey: id, p_answers: answersPayload(items, drafts) })
  if (error) throw error
  refreshWaiting() // 325: a count on the nav or the bell may have moved
}

export async function loadLiveResults(id: string): Promise<LiveResults> {
  const { data, error } = await supabase.rpc('survey_live_results', { p_survey: id })
  if (error) throw error
  const r = data as J
  return {
    surveyId: String(r.survey_id),
    title: str(r.title) ?? '',
    liveItemId: str(r.live_item_id),
    status: r.status as SurveyStatus,
    identified: r.identified === true,
    myAccess: (r.my_access as SurveyAccess | null) ?? null,
    submissions: Number(r.submissions ?? 0),
    suppressed: r.suppressed === true,
    minToShow: Number(r.min_to_show ?? 6),
    items: arr(r.items, (x) => {
      const i = x as J
      return {
        ...toItem(i),
        answered: i.answered == null ? null : Number(i.answered),
        people: i.people == null ? null : Number(i.people),
        counts: Array.isArray(i.counts) ? (i.counts as number[]) : null,
        scale: Array.isArray(i.scale) ? (i.scale as number[]) : null,
        answers: Array.isArray(i.answers)
          ? (i.answers as J[]).map((a) => ({
              answerId: str(a.answer_id),
              text: str(a.text) ?? '',
              name: str(a.name),
              at: str(a.at),
              hidden: a.hidden === true,
            }))
          : null,
        hiddenWords: arr(i.hidden_words, (w) => String(w)),
      }
    }),
  }
}

export async function createSurvey(title: string, description: string, identified: boolean): Promise<string> {
  const { data, error } = await supabase.rpc('create_survey', {
    p_title: title,
    p_description: description,
    p_identified: identified,
  })
  if (error) throw error
  return String(data)
}

export async function loadSurveyEditor(id: string): Promise<SurveyEditor> {
  const { data, error } = await supabase.rpc('survey_editor', { p_survey: id })
  if (error) throw error
  const r = data as J
  return {
    surveyId: String(r.survey_id),
    title: str(r.title) ?? '',
    description: str(r.description),
    identified: r.identified === true,
    state: r.state as SurveyStatus,
    status: r.status as SurveyStatus,
    archivedAt: str(r.archived_at),
    closesAt: str(r.closes_at),
    resultsToAudience: r.results_to_audience === true,
    linkAccess: r.link_access === true,
    openedAt: str(r.opened_at),
    submissions: Number(r.submissions ?? 0),
    answeredItems: arr(r.answered_items, (x) => String(x)),
    invitedCount: Number(r.invited_count ?? 0),
    items: arr(r.items, toItem),
    audience: arr(r.audience, (x) => {
      const a = x as J
      return {
        kind: a.kind as AudienceKind,
        orgUnitId: str(a.org_unit_id),
        personId: str(a.person_id),
        roleKey: str(a.role_key),
        label: str(a.label) ?? '',
      }
    }),
    groups: arr(r.groups, (x) => {
      const g = x as J
      return {
        whereChoices: arr(g.where_choices, String),
        whereIds: arr(g.where_ids, String),
        departmentIds: arr(g.department_ids, String),
        whereNames: arr(g.where_names, String),
        departmentNames: arr(g.department_names, String),
      }
    }),
    people: arr(r.people, (x) => {
      const p = x as J
      return { personId: String(p.person_id), access: p.access as SurveyAccess, name: str(p.name) ?? '' }
    }),
  }
}

export async function saveSurvey(
  id: string,
  fields: { title: string; description: string; identified: boolean; closesAt: string | null; resultsToAudience: boolean },
  items: EditorItem[],
) {
  const { error } = await supabase.rpc('save_survey', {
    p_survey: id,
    p_title: fields.title,
    p_description: fields.description,
    p_identified: fields.identified,
    p_closes_at: fields.closesAt,
    p_results_to_audience: fields.resultsToAudience,
    p_items: items.map((i) => ({
      item_id: i.itemId,
      kind: i.kind,
      prompt: i.prompt,
      choices: i.kind === 'single' || i.kind === 'multi' ? i.choices.map((c) => c.trim()).filter(Boolean) : [],
      required: i.required,
      display: i.display,
    })),
  })
  if (error) throw error
}

export async function setSurveyStatus(id: string, status: SurveyStatus) {
  const { error } = await supabase.rpc('set_survey_status', { p_survey: id, p_status: status })
  if (error) throw error
}

/** REPLACE who is asked: the lines and the groups, together (311). */
export async function setSurveyAudience(id: string, rows: AudienceRow[], groups: AudienceGroup[]) {
  const { error } = await supabase.rpc('set_survey_audience', {
    p_survey: id,
    p_rows: [
      ...rows.map((r) => ({ kind: r.kind, org_unit_id: r.orgUnitId, person_id: r.personId, role_key: r.roleKey })),
      ...groups.map((g) => ({ kind: 'group', where_choices: g.whereChoices, where_ids: g.whereIds, department_ids: g.departmentIds })),
    ],
  })
  if (error) throw error
}

/** 311. Anybody signed in who has the link (or the QR code) may answer. */
export async function setSurveyLinkAccess(id: string, on: boolean) {
  const { error } = await supabase.rpc('set_survey_link_access', { p_survey: id, p_on: on })
  if (error) throw error
}

/** A group in words: "Where: Canada, BU 51 and Department: Engineering". */
export function groupWords(
  g: Pick<AudienceGroup, 'whereChoices' | 'whereNames' | 'departmentNames'>,
  labelOf: (choice: string) => string,
): string {
  const where = g.whereChoices.length ? g.whereChoices.map(labelOf) : g.whereNames
  const parts: string[] = []
  if (where.length) parts.push(`Where: ${where.join(' or ')}`)
  if (g.departmentNames.length) parts.push(`Department: ${g.departmentNames.join(' or ')}`)
  return parts.join(' and ')
}

export async function setSurveyPeople(id: string, people: SurveyPerson[]) {
  const { error } = await supabase.rpc('set_survey_people', {
    p_survey: id,
    p_rows: people.map((p) => ({ person_id: p.personId, access: p.access })),
  })
  if (error) throw error
}

export async function hideSurveyWord(itemId: string, word: string, hidden: boolean) {
  const { error } = await supabase.rpc('hide_survey_word', { p_item: itemId, p_word: word, p_hidden: hidden })
  if (error) throw error
}

export async function hideSurveyAnswer(answerId: string, hidden: boolean) {
  const { error } = await supabase.rpc('hide_survey_answer', { p_answer: answerId, p_hidden: hidden })
  if (error) throw error
}

/** 314. Archive (closes it, off everybody's page but its owners' and viewers')
 *  or restore. Owners only. */
export async function setSurveyArchived(id: string, on: boolean) {
  const { error } = await supabase.rpc('set_survey_archived', { p_survey: id, p_on: on })
  if (error) throw error
}

/** 314. Any survey that is not open, with every answer in it. Cannot be undone. */
export async function deleteSurvey(id: string) {
  const { error } = await supabase.rpc('delete_survey', { p_survey: id })
  if (error) throw error
}

/** Roles anybody may name as an audience. `role` is member-readable (RLS). */
export async function loadRoleOptions(): Promise<{ key: string; name: string }[]> {
  const { data, error } = await supabase.from('role').select('key, name').order('name')
  if (error) throw error
  const seen = new Set<string>()
  return ((data ?? []) as { key: string; name: string }[]).filter((r) => !seen.has(r.key) && seen.add(r.key))
}

/** The link a QR code carries: the hub's own address and the survey's hash
 *  route. Scanned signed out, the sign-in screen keeps the hash, so the
 *  person lands on the survey once their code is in. */
export function surveyLink(origin: string, id: string): string {
  return `${origin.replace(/\/$/, '')}/#/surveys/${id}`
}

/** What the display shows when the item says 'auto'. */
export function displayFor(kind: ItemKind, display: ItemDisplay): Exclude<ItemDisplay, 'auto'> {
  if (display !== 'auto') return display
  if (kind === 'words') return 'cloud'
  if (kind === 'text') return 'list'
  if (kind === 'ideas') return 'pills'
  return 'bars'
}

/* ── The word cloud ──────────────────────────────────────────────────────────
   A "few words" answer is ONE term (a phrase, as people typed it, lower-cased
   and with spaces collapsed): "Curious", "curious " and "CURIOUS" are one.
   A longer answer is split into words, with the small ones and the common
   English and Spanish ones left out. A word the presenter hid never counts. */

const STOP = new Set(
  (
    'a an and are as at be but by for from has have i if in into is it its me my no not of on or our so ' +
    'that the their them then there these they this to too us was we were what when which who will with ' +
    'would you your can do just more very about also get all any been could should some than out up ' +
    'el la los las un una unos unas y o de del al en con por para que es son se lo le les mi mis tu su ' +
    'sus nos muy mas más pero como sin sobre este esta esto estos estas ese esa eso ya ha han hay ser'
  ).split(/\s+/),
)

export function normaliseTerm(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim()
}

export type CloudTerm = { word: string; count: number }

export function cloudTerms(kind: ItemKind, answers: ResultAnswer[], hiddenWords: string[], limit = 60): CloudTerm[] {
  const hidden = new Set(hiddenWords.map(normaliseTerm))
  const counts = new Map<string, number>()
  for (const a of answers) {
    if (a.hidden) continue
    const terms =
      kind === 'words'
        ? [normaliseTerm(a.text).replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')]
        : normaliseTerm(a.text)
            .split(/[^\p{L}\p{N}'-]+/u)
            .map((w) => w.replace(/^['-]+|['-]+$/g, ''))
            .filter((w) => w.length >= 3 && !STOP.has(w))
    for (const t of new Set(terms)) {
      if (!t || hidden.has(t)) continue
      counts.set(t, (counts.get(t) ?? 0) + 1)
    }
  }
  return [...counts.entries()]
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count || a.word.localeCompare(b.word))
    .slice(0, limit)
}

/** A font size between `min` and `max` rem, by the term's share of the top
 *  count. Square-root so one runaway word does not shrink the rest to dust. */
export function cloudSize(count: number, top: number, min = 1, max = 4.5): number {
  if (top <= 1) return (min + max) / 2
  const f = Math.sqrt((count - 1) / (top - 1))
  return Math.round((min + f * (max - min)) * 100) / 100
}

/** The biggest in the middle, then alternating out to either side — how a
 *  flex-wrap cloud reads as a cloud rather than a sorted list. */
export function cloudOrder<T>(sorted: T[]): T[] {
  const out: T[] = []
  sorted.forEach((t, i) => (i % 2 === 0 ? out.push(t) : out.unshift(t)))
  return out
}

/** Mean of a 1-5 rating from its five counts, one decimal; null when empty. */
export function scaleMean(scale: number[] | null): number | null {
  if (!scale) return null
  const n = scale.reduce((a, b) => a + b, 0)
  if (!n) return null
  return Math.round((scale.reduce((s, c, i) => s + c * (i + 1), 0) / n) * 10) / 10
}
